import React from 'react';
import styled from 'styled-components';
import { useShouldStackWorkCaption } from './useShouldStackWorkCaption';

type WorkViewCaptionProps = {
  artistName: string;
  title: string;
  prodYear: string;
  material: string;
  sizeOrDuration: string;
  maxWidth: string;
  centerVisible: boolean;
  $minHeight?: string;
  $marginBottom?: string;
};

const WorkViewCaption: React.FC<WorkViewCaptionProps> = ({
  artistName,
  title,
  prodYear,
  material,
  sizeOrDuration,
  maxWidth,
  centerVisible,
  $minHeight,
  $marginBottom,
}) => {
  const inlineInfo = prodYear + ', ' + material + ', ' + sizeOrDuration;
  const { availableRef, textStyleRef, shouldStack } = useShouldStackWorkCaption({
    artistName,
    title,
    inlineInfo,
  });

  const oneLine = (
    <>
      {artistName && <span>{artistName}, </span>}
      <CaptionTitle>{title}</CaptionTitle>, {inlineInfo}
    </>
  );

  return (
    <>
      <CaptionWidthProbe ref={availableRef} $maxWidth={maxWidth} />
      <InlineInfo
        ref={textStyleRef}
        $centerVisible={centerVisible}
        $maxWidth={maxWidth}
        $stacked={shouldStack}
        $minHeight={$minHeight}
        $marginBottom={$marginBottom}
      >
        {shouldStack ? (
          <CaptionStack>
            <CaptionLine>
              {artistName && <span>{artistName}, </span>}
              <CaptionTitle>{title}</CaptionTitle>
              {!!prodYear && <>, {prodYear}</>}
            </CaptionLine>
            {!!material && <CaptionLine>{material}</CaptionLine>}
            {!!sizeOrDuration && <CaptionLine>{sizeOrDuration}</CaptionLine>}
          </CaptionStack>
        ) : (
          oneLine
        )}
      </InlineInfo>
    </>
  );
};

const CaptionWidthProbe = styled.div<{ $maxWidth: string }>`
  width: min(${({ $maxWidth }) => $maxWidth}, 100%);
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
`;

const InlineInfo = styled.div<{
  $centerVisible: boolean;
  $maxWidth: string;
  $stacked: boolean;
  $minHeight?: string;
  $marginBottom?: string;
}>`
  box-sizing: border-box;
  width: ${({ $stacked }) => ($stacked ? '100%' : 'fit-content')};
  min-width: 0;
  max-width: min(${({ $maxWidth }) => $maxWidth}, 100%);
  height: ${({ $centerVisible }) => ($centerVisible ? 'auto' : '0')};
  min-height: ${({ $centerVisible, $minHeight }) =>
    $centerVisible ? ($minHeight ?? '0') : '0'};
  overflow: ${({ $centerVisible }) => ($centerVisible ? 'visible' : 'hidden')};
  display: block;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.45;
  margin-bottom: ${({ $centerVisible, $marginBottom }) =>
    $centerVisible ? ($marginBottom ?? '0') : '0'};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const CaptionStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const CaptionLine = styled.div`
  width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const CaptionTitle = styled.span`
  display: inline;
  font-style: oblique;
  transform: translateY(-1px);
  padding-top: 1px;
  margin-right: 2px;
`;

export default WorkViewCaption;
